import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Bot,
  MessageSquare,
  Shield,
  KeyRound,
  LogOut,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENGINE_MODELS = {
  chatgpt: [
    { value: "gpt-4o-mini", label: "GPT-4o Mini" },
    { value: "gpt-4o", label: "GPT-4o" },
    { value: "gpt-5.2", label: "GPT-5.2" },
  ],
  gemini: [
    { value: "gemini-2.0-flash", label: "Gemini 2.0 Flash" },
    { value: "gemini-3-flash", label: "Gemini 3 Flash" },
  ],
  deepseek: [
    { value: "deepseek-chat", label: "DeepSeek Chat" },
  ],
};

const ENGINE_OPTIONS = [
  { value: "chatgpt", label: "ChatGPT (OpenAI)" },
  { value: "gemini", label: "Gemini (Google)" },
  { value: "deepseek", label: "DeepSeek" },
];

export default function AdminDashboard({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [maskedKeys, setMaskedKeys] = useState({});

  const [form, setForm] = useState({
    system_prompt: "",
    ai_engine: "chatgpt",
    ai_model: "gpt-4o-mini",
    min_questions: 1,
    max_questions: 10,
    ip_daily_limit: 10,
    api_key_openai: "",
    api_key_gemini: "",
    api_key_deepseek: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      const data = res.data;
      setMaskedKeys({
        api_key_openai: data.api_key_openai,
        api_key_gemini: data.api_key_gemini,
        api_key_deepseek: data.api_key_deepseek,
      });
      setForm({
        system_prompt: data.system_prompt || "",
        ai_engine: data.ai_engine || "chatgpt",
        ai_model: data.ai_model || "gpt-4o-mini",
        min_questions: data.min_questions ?? 1,
        max_questions: data.max_questions ?? 10,
        ip_daily_limit: data.ip_daily_limit ?? 10,
        api_key_openai: "",
        api_key_gemini: "",
        api_key_deepseek: "",
      });
    } catch (err) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        system_prompt: form.system_prompt,
        ai_engine: form.ai_engine,
        ai_model: form.ai_model,
        min_questions: parseInt(form.min_questions) || 1,
        max_questions: parseInt(form.max_questions) || 10,
        ip_daily_limit: parseInt(form.ip_daily_limit) || 10,
      };
      // Only send API keys if user entered new values
      if (form.api_key_openai) payload.api_key_openai = form.api_key_openai;
      if (form.api_key_gemini) payload.api_key_gemini = form.api_key_gemini;
      if (form.api_key_deepseek) payload.api_key_deepseek = form.api_key_deepseek;

      const res = await axios.post(`${API}/settings`, payload, {
        headers: { "X-Admin-Password": "1diomas_test" },
      });

      setMaskedKeys({
        api_key_openai: res.data.api_key_openai,
        api_key_gemini: res.data.api_key_gemini,
        api_key_deepseek: res.data.api_key_deepseek,
      });
      setForm((prev) => ({
        ...prev,
        api_key_openai: "",
        api_key_gemini: "",
        api_key_deepseek: "",
      }));
      toast.success("Settings saved successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEngineChange = (engine) => {
    const models = ENGINE_MODELS[engine];
    updateField("ai_engine", engine);
    updateField("ai_model", models[0].value);
  };

  const availableModels = ENGINE_MODELS[form.ai_engine] || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-background bg-grid-dots">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-border/40 bg-background/80 backdrop-blur-lg">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-to-chat-btn"
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            </Button>
            <h1 className="font-heading text-xl font-semibold tracking-tight">
              Settings
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="save-settings-btn"
              onClick={handleSave}
              disabled={saving}
              className="bg-primary text-primary-foreground hover:bg-primary/90 glow-primary font-medium"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />
              )}
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              data-testid="admin-logout-btn"
              variant="ghost"
              size="icon"
              onClick={onLogout}
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 space-y-6 animate-fade-in">
        {/* AI Configuration */}
        <section className="rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <Bot className="h-5 w-5 text-primary" strokeWidth={1.5} />
            <h2 className="font-heading text-lg font-semibold">AI Configuration</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Engine</Label>
              <Select
                data-testid="engine-select"
                value={form.ai_engine}
                onValueChange={handleEngineChange}
              >
                <SelectTrigger data-testid="engine-select-trigger" className="bg-secondary/50 border-border/50">
                  <SelectValue placeholder="Select engine" />
                </SelectTrigger>
                <SelectContent>
                  {ENGINE_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Model</Label>
              <Select
                data-testid="model-select"
                value={form.ai_model}
                onValueChange={(v) => updateField("ai_model", v)}
              >
                <SelectTrigger data-testid="model-select-trigger" className="bg-secondary/50 border-border/50">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  {availableModels.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </section>

        {/* System Prompt */}
        <section className="rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <MessageSquare className="h-5 w-5 text-primary" strokeWidth={1.5} />
            <h2 className="font-heading text-lg font-semibold">System Prompt</h2>
          </div>
          <Textarea
            data-testid="system-prompt-textarea"
            value={form.system_prompt}
            onChange={(e) => updateField("system_prompt", e.target.value)}
            placeholder="Enter the system prompt for the AI..."
            rows={5}
            className="bg-secondary/50 border-border/50 focus:ring-primary resize-none font-body text-sm"
          />
        </section>

        {/* Conversation Limits & Rate Limiting */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <section className="rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm p-6">
            <div className="flex items-center gap-2 mb-5">
              <MessageSquare className="h-5 w-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-lg font-semibold">Conversation</h2>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Min Questions</Label>
                <Input
                  data-testid="min-questions-input"
                  type="number"
                  min="1"
                  value={form.min_questions}
                  onChange={(e) => updateField("min_questions", e.target.value)}
                  className="bg-secondary/50 border-border/50 focus:ring-primary"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Max Questions</Label>
                <Input
                  data-testid="max-questions-input"
                  type="number"
                  min="1"
                  value={form.max_questions}
                  onChange={(e) => updateField("max_questions", e.target.value)}
                  className="bg-secondary/50 border-border/50 focus:ring-primary"
                />
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm p-6">
            <div className="flex items-center gap-2 mb-5">
              <Shield className="h-5 w-5 text-primary" strokeWidth={1.5} />
              <h2 className="font-heading text-lg font-semibold">Rate Limiting</h2>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Daily Message Limit per IP</Label>
              <Input
                data-testid="ip-daily-limit-input"
                type="number"
                min="1"
                value={form.ip_daily_limit}
                onChange={(e) => updateField("ip_daily_limit", e.target.value)}
                className="bg-secondary/50 border-border/50 focus:ring-primary"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Maximum messages a single IP can send per day
              </p>
            </div>
          </section>
        </div>

        {/* API Keys */}
        <section className="rounded-xl border border-border/50 bg-card/60 backdrop-blur-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <KeyRound className="h-5 w-5 text-primary" strokeWidth={1.5} />
            <h2 className="font-heading text-lg font-semibold">API Keys</h2>
          </div>
          <div className="space-y-5">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">OpenAI API Key</Label>
                {maskedKeys.api_key_openai && maskedKeys.api_key_openai !== "" && (
                  <Badge variant="secondary" className="text-[10px] font-mono gap-1">
                    <CheckCircle2 className="h-3 w-3 text-green-400" />
                    Configured
                  </Badge>
                )}
              </div>
              <Input
                data-testid="api-key-openai-input"
                type="password"
                value={form.api_key_openai}
                onChange={(e) => updateField("api_key_openai", e.target.value)}
                placeholder={maskedKeys.api_key_openai || "sk-..."}
                className="bg-secondary/50 border-border/50 focus:ring-primary font-mono text-sm"
              />
            </div>
            <Separator className="bg-border/30" />
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">Gemini API Key</Label>
                {maskedKeys.api_key_gemini && maskedKeys.api_key_gemini !== "" && (
                  <Badge variant="secondary" className="text-[10px] font-mono gap-1">
                    <CheckCircle2 className="h-3 w-3 text-green-400" />
                    Configured
                  </Badge>
                )}
              </div>
              <Input
                data-testid="api-key-gemini-input"
                type="password"
                value={form.api_key_gemini}
                onChange={(e) => updateField("api_key_gemini", e.target.value)}
                placeholder={maskedKeys.api_key_gemini || "AIza..."}
                className="bg-secondary/50 border-border/50 focus:ring-primary font-mono text-sm"
              />
            </div>
            <Separator className="bg-border/30" />
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-muted-foreground">DeepSeek API Key</Label>
                {maskedKeys.api_key_deepseek && maskedKeys.api_key_deepseek !== "" && (
                  <Badge variant="secondary" className="text-[10px] font-mono gap-1">
                    <CheckCircle2 className="h-3 w-3 text-green-400" />
                    Configured
                  </Badge>
                )}
              </div>
              <Input
                data-testid="api-key-deepseek-input"
                type="password"
                value={form.api_key_deepseek}
                onChange={(e) => updateField("api_key_deepseek", e.target.value)}
                placeholder={maskedKeys.api_key_deepseek || "sk-..."}
                className="bg-secondary/50 border-border/50 focus:ring-primary font-mono text-sm"
              />
            </div>
          </div>
        </section>

        {/* Bottom Save */}
        <div className="flex justify-end pb-8">
          <Button
            data-testid="save-settings-bottom-btn"
            onClick={handleSave}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90 glow-primary font-medium px-8"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />
            )}
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </div>
      </div>
    </div>
  );
}
